<script setup lang="ts">
import { useRouteQuery } from "@vueuse/router";
import { AuthView } from "#imports";

const { isLoggedIn } = useAuth();
const config = useRuntimeConfig();

false && /*#__PURE__*/ defineI18nRoute({ paths: { pl: "/" } });
definePageMeta({ footerContained: true });
const route = useRoute();
const router = useRouter();

const localePath = useLocalePath();

// -----------------------------
// Posts

const page = useRouteQuery<number>("page", 1, {
  route,
  router,
  transform: Number,
  mode: "push",
});

useHead({
  link: () => [
    {
      rel: "canonical",
      href:
        config.public.baseUrl +
        localePath({
          name: "index",
          query: { page: page.value > 1 ? page.value : undefined },
        }),
    },
  ],
});

const limit = 20;

const { filter } = usePostFilter();

const { defaultPosts } = usePosts();
const { loadPosts } = usePostListing();

const {
  data: postsData,
  status: postsLoadingStatus,
  execute,
} = await useAsyncData(
  "posts:index",
  async () => loadPosts({ page, limit, filter }),
  { immediate: true, default: () => defaultPosts }
);

watch([page, filter], () => execute());

// -----------------------------
// Pagination

const total = computed(() => Number(postsData.value?.meta?.filter_count || 0));

// -----------------------------
// Grid items

const { postToGrid, splitItems } = useGrid();

const items = computed(() =>
  postsLoadingStatus.value === "pending"
    ? Array.from<"loading">({ length: limit }).fill("loading")
    : postsData.value.data.map((item, index) =>
        postToGrid(item, undefined, { lazyIndex: 2, index })
      )
);

const { isMobile } = useUserDevice();
const splitIndex = ref(8);

const updateSplitIndex = (isMobile: boolean) => {
  splitIndex.value = isMobile ? 5 : 8;
};

onMounted(() => updateSplitIndex(isMobile.value));
watch(isMobile, (isMobile) => updateSplitIndex(isMobile));

const { itemsBefore, itemsAfter } = splitItems<"loading" | GridItem>(
  items,
  splitIndex
);

// -----------------------------
// Breadcrumbs

const breadcrumbs = useBreadcrumbItems();

// -----------------------------
// Slider

const { getSlides } = usePageSlider();

const { data: slider, status: sliderStatus } = await useAsyncData(
  `slides:index`,
  async () => await getSlides("index"),
  { immediate: true }
);

const sliderItems = computed(() => slider.value || []);

// -----------------------------
// Schema.org

const { getPostLink } = usePosts();

useSchemaOrg([
  defineItemList({
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.value.reduce<any[]>((acc, item) => {
      if (item !== "loading" && item.post?.type && item.post?.slug) {
        const listItem = defineListItem({
          "@type": "ListItem",
          position: acc.length + 1,
          url:
            config.public.baseUrl + getPostLink(item.post.type, item.post.slug),
        });

        acc.push(listItem);
      }

      return acc;
    }, []),
  }),
]);

// -----------------------------
// CTA

const { getCtaContent } = useCta();

const { data: ctaContent } = await useAsyncData(
  `cta:index`,
  async () => await getCtaContent("index"),
  { immediate: true }
);

const showCta = computed(
  () =>
    !isLoggedIn.value &&
    !!ctaContent.value &&
    items.value.length > (isMobile.value ? 5 : 8)
);

// -----------------------------
// Footer

const { getFooterContent } = useFooter();

const { data: footerContent } = await useAsyncData(
  `footer:index`,
  async () => await getFooterContent("index"),
  { immediate: true }
);

// -----------------------------
// Auth

const { openAuthDialog } = useAuth();

// -----------------------------
// Loading

const isLoading = computed(() => postsLoadingStatus.value === "pending");

const resetPosts = () => {
  page.value = 1;
};

watch([filter], () => resetPosts());

watchImmediate(page, () => {
  if (import.meta.browser) {
    window.scrollTo({ top: 0, behavior: "instant" });
  }
});

// -----------------------------
// SEO

const title = computed(() =>
  page.value > 1 ? `(strona ${page.value})` : undefined
);

const { titleTemplate } = useSeo();
const ogTitle = computed(() => titleTemplate(title.value || undefined));

useSeoMeta({
  title: () => title.value as string,
  ogTitle,
});
</script>

<template>
  <PageBreadcrumbs v-bind="{ breadcrumbs }" />

  <div class="page page--index">
    <div class="page__wrapper grow">
      <template v-if="!isLoggedIn">
        <div v-if="sliderStatus === 'pending'" class="mb-4 lg:mb-5">
          <div class="container">
            <AppSkeleton
              block
              class="w-full !pt-[67.988668555%] lg:!pt-[34.264705882%] animate-pulse"
            />
          </div>
        </div>
        <LazyPageSlider
          v-else-if="sliderItems.length > 0"
          :items="sliderItems"
        />
      </template>

      <PageHeader>
        <template #start>
          <PostFilter />
        </template>

        <template #end>
          <PageTitle
            default="oglądasz"
            append="aktualności"
            mobile="aktualności"
            class="text-end"
          >
            <template v-if="page > 1" #additional>
              <span class="sr-only"> (strona {{ page }}) </span>
            </template>
          </PageTitle>
        </template>
      </PageHeader>

      <PostGrid
        id="post-grid-before"
        key="post-grid-before"
        :items="showCta ? itemsBefore : items"
        class="lg:mt-2.5 mb-10"
        container
        :loading="isLoading"
      />

      <ClientOnly>
        <PostGridCta
          v-if="showCta"
          id="post-grid-cta"
          key="post-grid-cta"
          :pre-title="ctaContent!.pre_title"
          :title="{ append: ctaContent!.title }"
          :content="ctaContent!.content?.split('\n\n') || []"
          :cta="{
            prompt: ctaContent!.action_prompt,
            button: ctaContent!.action_button,
          }"
          @click:action="openAuthDialog(AuthView.Register)"
          container
        />

        <PostGrid
          v-if="showCta"
          id="post-grid-after"
          key="post-grid-after"
          :items="itemsAfter"
          container
          class="mb-10"
          :loading="isLoading"
        />
      </ClientOnly>

      <AppPagination
        v-model="page"
        :items-per-page="limit"
        :total-items="total"
        :max-pages-shown="3"
        :show-ending-buttons="false"
        hide-prev-next-when-ends
        :link-url="localePath({ query: { ...route.query, page: '[page]' } })"
        class="my-10"
        container
      />
    </div>

    <AppFooterBefore
      v-if="footerContent"
      :pre-title="footerContent.pre_title"
      :title="{
        default: footerContent.title_normal,
        append: footerContent.title_decorative,
      }"
      :content="footerContent.content?.split('\n\n') || []"
    />
  </div>
</template>
